import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'
import ErrorIcon from '@mui/icons-material/Error'
import InfoIcon from '@mui/icons-material/Info'
import WarningIcon from '@mui/icons-material/Warning'
import { Box, CircularProgress, IconButton, Snackbar, Typography, alpha, useTheme } from '@mui/material'
import type { SnackbarOrigin } from '@mui/material/Snackbar'
import React, { createContext, useCallback, useContext, useState } from 'react'
import GroboticIcon from '../../components/Icons/GroboticIcon'

const iconMapping = {
  info: <InfoIcon fontSize="inherit" />,
  grobotic: <GroboticIcon />,
  success: <CheckCircleIcon sx={{ color: 'text.success' }} fontSize="inherit" />,
  error: <ErrorIcon sx={{ color: 'text.error' }} fontSize="inherit" />,
  warning: <WarningIcon sx={{ color: 'text.warning' }} fontSize="inherit" />,
}

const SnackbarAlert = ({ open, message, onClose, severity }) => {
  const theme = useTheme()
  // const backgroundColorDark = severity === 'grobotic' ? 'rgba(103, 128, 159, 0.5)' : ''
  const backgroundColorDark = 'background.snackbar'
  const backgroundColorLight = severity === 'grobotic' ? alpha(theme.palette.background.paper, 0.5) : ''
  const _backgroundColor = theme.palette.mode === 'dark' ? backgroundColorDark : backgroundColorLight
  // const broadcastColor = theme.palette.mode === 'dark' ? 'rgba(241, 130, 141, 1)' : 'rgba(241, 130, 141, 1)'
  const broadcastColor = theme.palette.text.warning
  const broadcastBorderColor = theme.palette.text.primary
  const darkBgBroadcast = severity === 'broadcast' ? 'rgb(28, 73, 128)' : ''
  const lightBgBroadcast = severity === 'broadcast' ? theme.palette.background.paper : ''
  const broadcastBgColor = theme.palette.mode === 'dark' ? darkBgBroadcast : lightBgBroadcast
  const _closingColor = theme.palette.text.primary
  const typeChangeBgColor = theme.palette.mode === 'dark' ? 'rgb(26, 22, 22)' : 'rgb(236, 239, 248)'

  const anchorOrigin: SnackbarOrigin =
    severity === 'broadcast' ? { vertical: 'top', horizontal: 'center' } : { vertical: 'bottom', horizontal: 'right' }

  const autoHideDuration = severity === 'broadcast' ? null : severity === 'update' ? 2000 : 3000
  function GradientCircularProgress() {
    return (
      <React.Fragment>
        <svg width={0} height={0}>
          <defs>
            <linearGradient id="my_gradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgb(106, 219, 138)" />
              <stop offset="100%" stopColor="rgb(17, 209, 210)" />
            </linearGradient>
          </defs>
        </svg>
        <CircularProgress size={18} sx={{ 'svg circle': { stroke: 'url(#my_gradient)' } }} />
      </React.Fragment>
    )
  }
  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      anchorOrigin={anchorOrigin}
      onClose={severity === 'broadcast' ? undefined : onClose}
      sx={{
        '& .MuiSnackbarContent-root': {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          padding: 0,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between', // Ensure space for the X button
          color: severity === 'broadcast' ? broadcastColor : '',
          border: severity === 'broadcast' ? `1px solid ${broadcastBorderColor}` : '',
          backgroundColor:
            severity === 'broadcast'
              ? broadcastBgColor
              : severity === 'typeWarning'
              ? typeChangeBgColor
              : 'background.defaultdim',
          borderRadius: '25px',
          pl: 1,
          pr: 2,
          width: '100%', // Adjust to make sure the box takes the full width
          backdropFilter: 'blur(15px)',
        }}
      >
        {severity === 'grobotic' ? (
          <>
            <GroboticIcon />
            <Box sx={{ ml: 1, flexGrow: 1 }}>{typeof message === 'string' ? message : 'An error occurred'}</Box>
          </>
        ) : severity === 'broadcast' ? (
          <>
            <GroboticIcon />
            <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', ml: 1 }}>
              {typeof message === 'string' ? (
                <Typography sx={{ fontWeight: 'bold' }}>{message}</Typography>
              ) : (
                'An error occurred'
              )}{' '}
            </Box>
            <IconButton color="inherit" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </>
        ) : severity === 'typeWarning' ? (
          <>
            <GroboticIcon />
            <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', ml: 1 }}>
              {typeof message === 'string' ? (
                <Typography sx={{ fontSize: 18, color: 'text.highlight' }}>{message}</Typography>
              ) : (
                'An error occurred'
              )}{' '}
            </Box>
            <IconButton color="inherit" onClick={onClose}>
              <WarningIcon sx={{ color: 'text.error' }} />
            </IconButton>
          </>
        ) : severity === 'update' ? (
          <>
            <GradientCircularProgress />
            {message || ''}
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 1, p: 2 }}>
            <Typography sx={{ fontSize: 'inherit', height: '100%' }}>
              {iconMapping[severity] || iconMapping.info}
            </Typography>
            <Typography sx={{ fontSize: 'inherit', height: '100%' }}>{message}</Typography>
          </Box>
        )}
      </Box>
    </Snackbar>
  )
}
/*


*/
const SnackbarContext = createContext({
  showSnackbar: (message, severity) => {},
})

export const useSnackbarContext = () => useContext(SnackbarContext)

export const SnackbarProvider = ({ children }) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  })

  const showSnackbar = useCallback((message, severity = 'info') => {
    // console.log('[showSnackbar] message', message, 'severity', severity)
    // setSnackbar({
    //   open: true,
    //   message: typeof message === 'string' ? message : JSON.stringify(message),
    //   severity,
    // })
    setSnackbar({
      open: true,
      message, // Directly pass the message, whether it's a string or JSX
      severity,
    })
  }, [])

  const closeSnackbar = useCallback(() => {
    setSnackbar({ open: false, message: '', severity: '' })
  }, [])

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <SnackbarAlert
        open={snackbar.open}
        message={snackbar.message}
        onClose={closeSnackbar}
        severity={snackbar.severity}
      />
    </SnackbarContext.Provider>
  )
}
